import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { ComposableMap, Geographies, Geography, Marker, ZoomableGroup } from "react-simple-maps";
import { scaleSqrt } from "d3-scale";
import api from "@/lib/api";
import { usePersistedBool } from "@/lib/collassabile";
import { Globe2, Loader2, ZoomIn, ZoomOut, Locate, ShieldAlert, X, ExternalLink , ChevronDown, ChevronRight } from "lucide-react";

const GEO_URL = "/world-110m.json";
const NAVY = "#1A2B4A";
const GOLD = "#C9A84C";
const HOME = { coordinates: [12, 30], zoom: 1 };

const CAT_COLORS = {
  conflitto_armato: "#9b2c2c", persecuzione_politica: "#C2410C", persecuzione_etnico_religiosa: "#B45309",
  gruppi_sociali: "#7C3AED", genere_orientamento: "#DB2777", pena_morte: "#111827",
  tortura: "#B91C1C", detenzione_sparizioni: "#4338CA", sfollamento: "#0E7490", assenza_protezione: "#4B5563",
};
const LIVELLO_R = { elevata: 26, significativa: 19, approfondimento: 14, insufficiente: 11 };
const LIVELLO_LABEL = {
  elevata: "Rilevanza COI elevata", significativa: "Rilevanza COI significativa",
  approfondimento: "Approfondimento necessario", insufficiente: "Dati insufficienti o non aggiornati",
};
const AMBITO_LABEL = {
  nazionale: "Rischio generalizzato nazionale", regionale: "Rischio limitato a una regione",
  profilo: "Rischio collegato a profili personali", contestuale: "Informazione contestuale",
};
const WARNING = "Le informazioni geografiche e le Country of Origin Information costituiscono esclusivamente un supporto documentale. La presenza di conflitti, violenze o violazioni dei diritti umani non determina automaticamente il riconoscimento della protezione internazionale. Ogni domanda deve essere valutata individualmente da un professionista qualificato e dalle autorità competenti.";
const norm = (s) => (s || "").trim().toUpperCase();

function MiniStat({ label, value, accent }) {
  return (
    <div className="bg-[#f9f8f5] border border-[#1A2B4A]/10 rounded-md px-3 py-2.5" data-testid={`geo-stat-${label}`}>
      <div className="text-[10px] uppercase tracking-[0.08em] text-[#4a5568] leading-tight">{label}</div>
      <div className={`text-lg font-semibold leading-none mt-1 ${accent ? "text-[#C9A84C]" : "text-[#1A2B4A]"}`}>{value}</div>
    </div>
  );
}

function COIDetail({ item, catLabel, onClose }) {
  const color = CAT_COLORS[item.categoria] || NAVY;
  return (
    <div className="fixed inset-0 z-[70] bg-[#1a1a1a]/50 flex items-center justify-center p-4" data-testid="coi-detail-modal" onClick={onClose}>
      <div className="bg-white rounded-lg max-w-lg w-full max-h-[90vh] overflow-y-auto p-4 border-t-2" style={{ borderColor: color }} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3 mb-3">
          <div>
            <div className="text-xs uppercase tracking-wide" style={{ color }}>{catLabel(item.categoria)}</div>
            <h3 className="font-serif text-xl text-[#1A2B4A]">{item.paese}{item.regione ? ` · ${item.regione}` : ""}</h3>
          </div>
          <button onClick={onClose} className="text-[#4a5568] hover:text-[#1A2B4A]" data-testid="coi-detail-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="flex flex-wrap gap-2 mb-3">
          <span className="text-[11px] px-2 py-1 rounded-md text-white" style={{ backgroundColor: color }}>{LIVELLO_LABEL[item.livello] || item.livello}</span>
          <span className="text-[11px] px-2 py-1 rounded-md bg-[#f0ede4] text-[#4a5568]">{AMBITO_LABEL[item.ambito] || item.ambito}</span>
          {item.scaduto && <span className="text-[11px] px-2 py-1 rounded-md bg-[#faf5e6] text-[#8a6d1f]">Dati non aggiornati</span>}
        </div>
        <dl className="text-sm space-y-2">
          {item.profili && <div><dt className="text-xs text-[#4a5568]">Profili potenzialmente esposti</dt><dd className="text-[#1A2B4A]">{item.profili}</dd></div>}
          {item.descrizione && <div><dt className="text-xs text-[#4a5568]">Descrizione</dt><dd className="text-[#1A2B4A]">{item.descrizione}</dd></div>}
          {item.periodo_riferimento && <div><dt className="text-xs text-[#4a5568]">Periodo di riferimento</dt><dd className="text-[#1A2B4A]">{item.periodo_riferimento}</dd></div>}
          <div><dt className="text-xs text-[#4a5568]">Fonte</dt><dd className="text-[#1A2B4A]">{item.fonte || "—"}</dd></div>
          <div className="grid grid-cols-2 gap-2">
            <div><dt className="text-xs text-[#4a5568]">Ultimo controllo</dt><dd className="text-[#1A2B4A]">{item.data_ultimo_controllo || "—"}</dd></div>
            <div><dt className="text-xs text-[#4a5568]">Revisione prevista</dt><dd className="text-[#1A2B4A]">{item.data_revisione || "—"}</dd></div>
          </div>
        </dl>
        {item.url && <a href={item.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 mt-3 text-sm text-[#1A2B4A] underline hover:text-[#C9A84C]" data-testid="coi-detail-link"><ExternalLink className="w-4 h-4" /> Documento originale</a>}
        <p className="text-[11px] text-[#4a5568] mt-4 border-t border-[#1A2B4A]/10 pt-3">{WARNING}</p>
      </div>
    </div>
  );
}

export function ContactsMap({ senzaCornice }) {
  const [mode, setMode] = useState("origine");
  const [pos, setPos] = useState(HOME);
  const [hover, setHover] = useState(null);
  const [coiOn, setCoiOn] = useState(false);
  const [activeCats, setActiveCats] = useState(null); // null = all
  const [selected, setSelected] = useState(null);
  const navigate = useNavigate();

  const { data, isLoading } = useQuery({
    queryKey: ["dashboard-geo", mode],
    queryFn: async () => (await api.get(`/dashboard/geo?mode=${mode}`)).data, staleTime: 300000,
  });
  const { data: coiMeta } = useQuery({ queryKey: ["coi-meta"], queryFn: async () => (await api.get("/coi/categories")).data });
  const { data: coiData } = useQuery({ queryKey: ["coi-map"], queryFn: async () => (await api.get("/coi/indicators")).data, staleTime: 300000 });

  const cats = coiMeta?.categories || [];
  const catLabel = (k) => cats.find((c) => c.key === k)?.label || k;
  const isCatOn = (k) => activeCats === null || activeCats.has(k);
  const toggleCat = (k) => setActiveCats((prev) => {
    const base = prev === null ? new Set(cats.map((c) => c.key)) : new Set(prev);
    if (base.has(k)) base.delete(k); else base.add(k);
    return base;
  });

  const bubbles = data?.bubbles || [];
  const maxCount = bubbles.reduce((m, b) => Math.max(m, b.count), 1);
  const rScale = useMemo(() => scaleSqrt().domain([1, Math.max(maxCount, 1)]).range([6, 30]), [maxCount]);

  const coiAll = (coiData?.indicators || []).filter((i) => i.lat != null && i.lon != null);
  const coiVisible = coiOn ? coiAll.filter((i) => isCatOn(i.categoria)) : [];
  const coiUpdated = coiData?.updated_at ? new Date(coiData.updated_at).toLocaleDateString("it-IT") : null;

  const hoveredCountry = hover ? [hover.country, hover.query] : [];
  const coiMatch = (i) => hoveredCountry.some((c) => c && (norm(i.paese) === norm(c) || norm(c).includes(norm(i.paese)) || norm(i.paese).includes(norm(c))));
  const hoverCoiCount = hover ? coiVisible.filter((i) => !i.scaduto && coiMatch(i)).length : 0;

  const [apertoLocale, setAperto] = usePersistedBool("home_contacts_map_open", true);
  const aperto = senzaCornice ? true : apertoLocale;

  const zoomIn = () => setPos((p) => ({ ...p, zoom: Math.min(p.zoom * 1.6, 8) }));
  const zoomOut = () => setPos((p) => ({ ...p, zoom: Math.max(p.zoom / 1.6, 1) }));
  const reset = () => setPos(HOME);

  const azioni = (
    <>
      <button data-testid="coi-layer-toggle" onClick={() => setCoiOn((v) => !v)}
        className={`btn btn-piccolo ${coiOn ? "btn-p" : "btn-o"}`}>
        <ShieldAlert className="w-3.5 h-3.5" /> Rilevanza COI
      </button>
      {[["origine", "Origine"], ["residenza", "Residenza"]].map(([m, l]) => (
        <button key={m} data-testid={`geo-mode-${m}`} onClick={() => { setMode(m); reset(); }}
          className={`btn btn-piccolo ${mode === m ? "btn-p" : "btn-o"}`}>{l}</button>
      ))}
    </>
  );

  return (
    <div data-testid="contacts-map-card"
      className={senzaCornice ? "" : "mb-5 bg-white border border-[#1A2B4A]/10 rounded-lg overflow-hidden shadow-sm"}>
      {senzaCornice ? (
        <div className="flex flex-wrap items-center justify-end gap-2.5 px-4 pt-3.5" data-testid="geo-mode-switch">{azioni}</div>
      ) : (
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-[#1A2B4A]/8 flex-wrap gap-3">
        <button data-testid="contacts-map-toggle" onClick={() => setAperto(!aperto)} className="flex items-center gap-2 text-[#1A2B4A] text-left">
          {aperto ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          <Globe2 className="w-4 h-4 text-[#C9A84C]" />
          <span className="text-xl font-semibold tracking-tight">I clienti di RB STUDIO</span>
        </button>
        {aperto && <div className="flex items-center gap-2 flex-wrap" data-testid="geo-mode-switch">{azioni}</div>}
      </div>
      )}

      {aperto && (
      <div className="p-4 sm:p-4">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-4">
          <MiniStat label="Rappresentati" value={`${data?.represented ?? 0}/${data?.total ?? 0}`} accent />
          <MiniStat label="Paesi" value={data?.countries ?? 0} />
          <MiniStat label="Località" value={data?.localities ?? 0} />
          <MiniStat label="Senza geo" value={data?.unplaceable ?? 0} />
          <MiniStat label="Località top" value={data?.top5?.[0]?.label || "—"} />
        </div>

        <div className="grid lg:grid-cols-[1fr_260px] gap-4">
          <div className="relative rounded-lg overflow-hidden border border-[rgba(26,43,74,0.08)] bg-[#eef2f6]" data-testid="contacts-map">
            {isLoading && (
              <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/70">
                <Loader2 className="w-5 h-5 animate-spin text-[#C9A84C]" /><span className="text-xs text-[#4a5568] ml-2">Geocodifica in corso…</span>
              </div>
            )}
            <ComposableMap projection="geoEqualEarth" projectionConfig={{ scale: 165 }} style={{ width: "100%", height: "auto" }}>
              <ZoomableGroup zoom={pos.zoom} center={pos.coordinates} minZoom={1} maxZoom={8} onMoveEnd={(p) => setPos(p)}>
                <Geographies geography={GEO_URL}>
                  {({ geographies }) => geographies.map((geo) => (
                    <Geography key={geo.rsmKey} geography={geo} fill="#e2e0d6" stroke="#ffffff" strokeWidth={0.4}
                      style={{ default: { outline: "none" }, hover: { fill: "#d8d5c7", outline: "none" }, pressed: { outline: "none" } }} />
                  ))}
                </Geographies>

                {/* COI overlay (semi-trasparente, sotto le bolle contatti) */}
                {coiVisible.map((i) => {
                  const color = CAT_COLORS[i.categoria] || NAVY;
                  const base = LIVELLO_R[i.livello] || 12;
                  const r = Math.max(base / pos.zoom, 4);
                  const dim = i.scaduto;
                  const hl = hover && coiMatch(i) && !dim;
                  return (
                    <Marker key={i.id} coordinates={[i.lon, i.lat]}
                      onClick={() => setSelected(i)} style={{ default: { cursor: "pointer" } }} data-testid={`coi-area-${i.id}`}>
                      <circle r={r} fill={color} fillOpacity={dim ? 0.1 : 0.22}
                        stroke={color} strokeOpacity={dim ? 0.3 : 0.75} strokeWidth={(hl ? 2.4 : 1.2) / pos.zoom}
                        strokeDasharray={`${3 / pos.zoom},${2 / pos.zoom}`} />
                    </Marker>
                  );
                })}

                {/* Bolle contatti (sopra) */}
                {bubbles.map((b) => {
                  const r = Math.max(rScale(b.count) / pos.zoom, 2.5);
                  return (
                    <Marker key={b.query} coordinates={[b.lon, b.lat]}
                      onMouseEnter={(e) => setHover({ ...b, x: e.clientX, y: e.clientY })}
                      onMouseMove={(e) => setHover((h) => (h ? { ...h, x: e.clientX, y: e.clientY } : h))}
                      onMouseLeave={() => setHover(null)}
                      onClick={() => navigate(`/clienti?loc=${encodeURIComponent(b.query)}&mode=${mode}`)}
                      style={{ default: { cursor: "pointer" }, hover: { cursor: "pointer" } }} data-testid={`geo-bubble-${b.query}`}>
                      <circle r={r} fill={GOLD} fillOpacity={0.6} stroke={NAVY} strokeWidth={1.4 / pos.zoom} />
                      <circle r={Math.max(r * 0.28, 1)} fill={NAVY} />
                    </Marker>
                  );
                })}
              </ZoomableGroup>
            </ComposableMap>

            <div className="absolute top-3 right-3 flex flex-col gap-1.5">
              <button data-testid="geo-zoom-in" onClick={zoomIn} className="w-8 h-8 rounded-lg bg-white/95 border border-[#1A2B4A]/15 flex items-center justify-center text-[#1A2B4A] hover:border-[#C9A84C] shadow-sm"><ZoomIn className="w-4 h-4" /></button>
              <button data-testid="geo-zoom-out" onClick={zoomOut} className="w-8 h-8 rounded-lg bg-white/95 border border-[#1A2B4A]/15 flex items-center justify-center text-[#1A2B4A] hover:border-[#C9A84C] shadow-sm"><ZoomOut className="w-4 h-4" /></button>
              <button data-testid="geo-reset" onClick={reset} title="Vista mondiale" className="w-8 h-8 rounded-lg bg-white/95 border border-[#1A2B4A]/15 flex items-center justify-center text-[#1A2B4A] hover:border-[#C9A84C] shadow-sm"><Locate className="w-4 h-4" /></button>
            </div>

            {!isLoading && bubbles.length === 0 && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <p className="text-sm text-[#4a5568] bg-white/80 px-4 py-2 rounded-lg">Nessun dato geografico disponibile per questa vista.</p>
              </div>
            )}
          </div>

          <div className="rounded-lg border border-[#1A2B4A]/10 p-4" data-testid="geo-top5">
            <div className="text-[11px] font-semibold uppercase tracking-[0.1em] text-[#C9A84C] mb-3">Top 5 località</div>
            {(data?.top5 || []).length === 0 ? (
              <p className="text-sm text-[#4a5568]">Nessuna località.</p>
            ) : (
              <ul className="space-y-2">
                {data.top5.map((t, i) => (
                  <li key={t.query || t.label} className="flex items-center justify-between gap-2 text-sm">
                    <span className="truncate text-[#1A2B4A]"><span className="text-[#C9A84C] font-semibold mr-1">{i + 1}.</span>{t.label}
                      {t.country && t.country !== t.label ? <span className="text-[#4a5568]"> · {t.country}</span> : null}</span>
                    <span className="shrink-0 text-[#4a5568]">{t.count} <span className="text-[10px]">({t.percent}%)</span></span>
                  </li>
                ))}
              </ul>
            )}
            <p className="text-[11px] text-[#4a5568] mt-4">Dati aggregati per privacy. Clicca una bolla per filtrare i contatti di quella località.</p>
          </div>
        </div>

        {/* Legenda COI */}
        {coiOn && (
          <div className="mt-4 rounded-lg border border-[#9b2c2c]/20 bg-[#fdf7f7] p-4" data-testid="coi-legend">
            <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
              <span className="text-[11px] font-semibold uppercase tracking-[0.1em] text-[#9b2c2c]">Livello Rilevanza COI · categorie</span>
              {coiUpdated && <span className="text-[10px] text-[#4a5568]">Aggiornato: {coiUpdated}</span>}
            </div>
            <div className="flex flex-wrap gap-2">
              {cats.map((c) => {
                const on = isCatOn(c.key);
                const col = CAT_COLORS[c.key];
                return (
                  <button key={c.key} data-testid={`coi-cat-${c.key}`} onClick={() => toggleCat(c.key)}
                    className={`inline-flex items-center gap-1.5 text-[11px] px-2.5 py-1 rounded-md border transition-all ${on ? "text-[#1A2B4A]" : "text-[#4a5568] opacity-45"}`}
                    style={{ borderColor: col }}>
                    <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: col }} /> {c.label}
                  </button>
                );
              })}
            </div>
            {coiVisible.length === 0 && <p className="text-xs text-[#4a5568] mt-2">Nessun indicatore COI per le categorie attive. Aggiungili in Impostazioni → Rilevanza COI.</p>}
          </div>
        )}

        <p className="text-[11px] text-[#4a5568] mt-4 border-t border-[#1A2B4A]/10 pt-3" data-testid="coi-warning">{WARNING}</p>
      </div>
      )}

      {hover && (
        <div className="fixed z-[60] pointer-events-none bg-[#1A2B4A] text-white text-xs rounded-lg px-3 py-2 shadow-xl" style={{ left: hover.x + 14, top: hover.y + 14 }} data-testid="geo-tooltip">
          <div className="font-semibold">{hover.label}{hover.country && hover.country !== hover.label ? `, ${hover.country}` : ""}</div>
          <div className="mt-0.5">{hover.count} contatti · {hover.percent}%</div>
          {coiOn && hoverCoiCount > 0 && <div className="text-[10px] text-[#f0b0b0] mt-0.5">COI: {hoverCoiCount} indicator{hoverCoiCount === 1 ? "e" : "i"} · approfondimento</div>}
          <div className="text-[10px] text-[#C9A84C] mt-0.5">Clicca per filtrare i contatti</div>
        </div>
      )}

      {selected && <COIDetail item={selected} catLabel={catLabel} onClose={() => setSelected(null)} />}
    </div>
  );
}
